import React, {useEffect, useState} from "react";
import styled from "styled-components";
import {Controller, useForm} from "react-hook-form";
import { allCountries, postalCodePatterns } from "../../utils/countryValidation";
import { useTranslation } from "../../hooks/useTranslation";
import { fieldConfig } from "../../i18n/utils/constants";
import { useCheckout } from "../../context/CheckoutContext";

const FormSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 15px;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 5px;
  display: flex;
  gap: 2px:
  align-items: center;
`;

const Required = styled.span`
  font-size : 16px;
  color: red;
  margin-left: 4px;
`;

const ValidationStatus = styled.span`
  font-size: 12px;
  color: ${(props) => (props.$isValid ? "#4CAF50" : "#f44336")};
  margin-left: 5px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid ${(props) =>
    props.$isInvalid ? "#f44336" : props.$isValid ? "#4CAF50" : "#c9c7c7"};
  border-radius: 4px;
  font-size: 16px;

  background-color: ${(props) => props.disabled ? "#f3f4f6" : "white"};
  cursor: ${(props) => props.disabled ? "not-allowed" : "text"};
  opacity: ${(props) => props.disabled ? 0.6 : 1};

  &:focus {
    outline: none;
    border-color: ${(props) =>
    props.$isInvalid ? "#f44336" : props.$isValid ? "#4CAF50" : "#3a75c4"};
  }
`;

const Select = styled.select`
  padding: 10px;
  padding-right: 30px;
  border: 1px solid ${(props) =>
    props.$isInvalid ? "#f44336" : props.$isValid ? "#4CAF50" : "#ddd"};
  border-radius: 4px;
  font-size: 16px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;

  background-color: ${(props) => props.disabled ? "#f3f4f6" : "white"};
  cursor: ${(props) => props.disabled ? "not-allowed" : "auto"};
  opacity: ${(props) => props.disabled ? 0.6 : 1};

  &:focus {
    outline: none;
    border-color: ${(props) =>
    props.$isInvalid ? "#f44336" : props.$isValid ? "#4CAF50" : "#3a75c4"};
  }
`;

const ValidationMessage = styled.div`
  color: ${(props) => (props.$isValid ? "#4CAF50" : "#f44336")};
  font-size: 12px;
  margin-top: 5px;
  min-height: 18px;
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;

  input {
    margin-right: 10px;
  }

  label {
    font-size: 14px;
  }
`;

// Email regex pattern
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const CustomerInfoForm = ({
  formData,
  onChange,
  checkoutPageFields,
  formValidations,
  itemDetails,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { clientCountry } = useCheckout();
  const [shippingAddressEnabled, setShippingAddressEnabled] = useState(false);
  const {
    control,
    watch,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: formData,
    mode: "onChange",
  });
  const useSameShippingAddress = watch("useSameShippingAddress");
  const billingCountry = watch("billingAddress?.country");
  const shippingCountry = watch("shippingAddress?.country");
  const email = watch("email");
  const billingZipCode = watch("billingAddress?.zipCode");
  const shippingZipCode = watch("shippingAddress?.zipCode");
  const billingState = watch("billingAddress.state");

  const fieldValidationMap = {
    FIRST_NAME: "firstName",
    LAST_NAME: "lastName",
    EMAIL: "email",

    BILLING_FIRST_NAME: "billingFirstName",
    BILLING_LAST_NAME: "billingLastName",
    BILLING_CITY: "billingCity",
    BILLING_ADDRESS_LINE_1: "billingAddressLine1",
    BILLING_ADDRESS_LINE_2: "billingAddressLine2",
    BILLING_COUNTRY: "billingCountry",
    BILLING_ZIPCODE: "billingZipCode",
    BILLING_STATE: "billingState",

    SHIPPING_FIRST_NAME: "shippingFirstName",
    SHIPPING_LAST_NAME: "shippingLastName",
    SHIPPING_CITY: "shippingCity",
    SHIPPING_ADDRESS_LINE_1: "shippingAddressLine1",
    SHIPPING_ADDRESS_LINE_2: "shippingAddressLine2",
    SHIPPING_COUNTRY: "shippingCountry",
    SHIPPING_ZIPCODE: "shippingZipCode",
    SHIPPING_STATE: "shippingState",
  };

  const [validations, setValidations] = useState({});
  const isBRLCurrency = itemDetails?.currencyCode === 'BRL';


  useEffect(() => {
    if (!checkoutPageFields?.length) return;

    const initial = checkoutPageFields.reduce((acc, field) => {
      const key = fieldValidationMap[field];
      if (key) acc[key] = { isValid: false, message: "" };
      return acc;
    }, {});

    // Base fields — respect required config
    initial.firstName = { isValid: !isFieldRequired("FIRST_NAME") };
    initial.lastName = { isValid: !isFieldRequired("LAST_NAME") };
    initial.email = { isValid: !isFieldRequired("EMAIL"), message: "" };

    // Add billingState if BRL
    if (isBRLCurrency) {
      initial.billingState = { isValid: !isFieldRequired("BILLING_STATE"), message: "" };
    }

    setValidations(initial);
  }, [checkoutPageFields, isBRLCurrency]);

  // Brazil states for BRL currency
  const brazilStates = [
    { name: t('states.brazil.AC'), code: "AC" },
    { name: t('states.brazil.AL'), code: "AL" },
    { name: t('states.brazil.AP'), code: "AP" },
    { name: t('states.brazil.AM'), code: "AM" },
    { name: t('states.brazil.BA'), code: "BA" },
    { name: t('states.brazil.CE'), code: "CE" },
    { name: t('states.brazil.DF'), code: "DF" },
    { name: t('states.brazil.ES'), code: "ES" },
    { name: t('states.brazil.GO'), code: "GO" },
    { name: t('states.brazil.MA'), code: "MA" },
    { name: t('states.brazil.MT'), code: "MT" },
    { name: t('states.brazil.MS'), code: "MS" },
    { name: t('states.brazil.MG'), code: "MG" },
    { name: t('states.brazil.PA'), code: "PA" },
    { name: t('states.brazil.PB'), code: "PB" },
    { name: t('states.brazil.PR'), code: "PR" },
    { name: t('states.brazil.PE'), code: "PE" },
    { name: t('states.brazil.PI'), code: "PI" },
    { name: t('states.brazil.RJ'), code: "RJ" },
    { name: t('states.brazil.RN'), code: "RN" },
    { name: t('states.brazil.RS'), code: "RS" },
    { name: t('states.brazil.RO'), code: "RO" },
    { name: t('states.brazil.RR'), code: "RR" },
    { name: t('states.brazil.SC'), code: "SC" },
    { name: t('states.brazil.SP'), code: "SP" },
    { name: t('states.brazil.SE'), code: "SE" },
    { name: t('states.brazil.TO'), code: "TO" },
  ];


  const getZipCodePattern = (country) => {
    return postalCodePatterns[country] || postalCodePatterns.default;
  };

  // Auto-set country to Brazil when currency is BRL.
  // `checkoutPageFields` is included in the deps because the currency (and thus
  // isBRLCurrency) can resolve before the field list loads (e.g. hosted-page
  // flow). Without it, this effect runs too early, shouldShowField() is false,
  // and the country is never set — leaving BILLING_COUNTRY perpetually invalid.
  useEffect(() => {
    if (isBRLCurrency && shouldShowField("BILLING_COUNTRY")) {
      if (formData?.billingAddress?.country !== 'BR') {
        handleInputChange("billingAddress.country", "BR");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBRLCurrency, formData?.billingAddress?.country, checkoutPageFields]);

  // Preselect the country dropdown with the IP-derived country when:
  //   - the BILLING_COUNTRY field is shown,
  //   - the consumer has not picked a country yet,
  //   - we have a country from the IP geo lookup,
  //   - and no higher-priority default applies (e.g. BRL -> BR).
  // The user remains free to change the selection afterwards.
  useEffect(() => {
    if (!clientCountry) return;
    if (!shouldShowField("BILLING_COUNTRY")) return;
    if (isBRLCurrency) return;
    if (formData?.billingAddress?.country) return;

    const supportedCountry = allCountries.find(
      (c) => c.code === clientCountry
    );
    if (!supportedCountry) return;

    handleInputChange("billingAddress.country", clientCountry);
    // handleInputChange intentionally omitted — it's redefined every render
    // and its behavior is pure on (field, value).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientCountry,
    isBRLCurrency,
    checkoutPageFields,
    formData?.billingAddress?.country,
  ]);

  const isFieldRequired = (field) => {
    return fieldConfig[field]?.required === true;
  };


  const getDynamicFormValidity = () => {
    if (!checkoutPageFields || checkoutPageFields.length === 0) return false;

    const dynamicFieldsValid = checkoutPageFields.every((field) => {
      const key = fieldValidationMap[field];
      if (!key) return true;

      if (!isFieldRequired(field)) return true; // ignore optional fields

      return validations?.[key]?.isValid === true;
    });

    // ✅ Always required fields
    const baseFieldsValid =
      validations?.firstName?.isValid &&
      validations?.lastName?.isValid &&
      validations?.email?.isValid;

    // ✅ BRL condition
    const billingStateValid = isBRLCurrency
      ? validations?.billingState?.isValid
      : true;

    return dynamicFieldsValid && baseFieldsValid && billingStateValid;
  };

  useEffect(() => {
    const isValid = getDynamicFormValidity();
    formValidations({
      ...validations,
      isFormValid: isValid,
    });
  }, [validations, checkoutPageFields, formData.useSameShippingAddress]);

  useEffect(() => {
    if (!checkoutPageFields || checkoutPageFields.length === 0) return;

    const newValidations = {};

    checkoutPageFields.forEach((field) => {
      const key = fieldValidationMap[field];
      if (!key) return;

      let value;
      if (key.startsWith("billing")) {
        let subKey = key.replace("billing", "").replace(/^./, (c) => c.toLowerCase());
        // Form data uses `street` while validation key is `AddressLine1`.
        if (subKey === "addressLine1") subKey = "street";
        value = formData?.billingAddress?.[subKey];
      } else if (key.startsWith("shipping")) {
        let subKey = key.replace("shipping", "").replace(/^./, (c) => c.toLowerCase());
        // Form data uses `street` while validation key is `AddressLine1`.
        if (subKey === "addressLine1") subKey = "street";
        value = formData?.shippingAddress?.[subKey];
      } else {
        value = formData?.[key];
      }

      // ✅ Skip shipping validation if same as billing
      if (field.startsWith("SHIPPING") && formData.useSameShippingAddress) {
        const billingKey = field.replace("SHIPPING", "BILLING");
        const billingMapped = fieldValidationMap[billingKey];

        newValidations[key] = newValidations[billingMapped] || {
          isValid: true,
          message: "",
        };
        return;
      }

      // Email validation
      if (key === "email") {
        const required = isFieldRequired("EMAIL");

        const isValid = required
          ? emailPattern.test(value || "")
          : value
          ? emailPattern.test(value)
          : true;

        newValidations[key] = {
          isValid,
          message: isValid
            ? t('checkout.validation.validField')
            : required
            ? t('checkout.validation.invalidEmail')
            : "",
        };
      }
      // Zip code validation
      else if (key.toLowerCase().includes("zipcode")) {
        const countryKey = key.startsWith("billing") ? formData?.billingAddress?.country : formData?.shippingAddress?.country;
        const required = isFieldRequired(field);

        if (!required && !value) {
          newValidations[key] = { isValid: true, message: "" };
        } else if (key.toLowerCase().includes("zipcode")) {
          const countryKey = key.startsWith("billing")
            ? formData?.billingAddress?.country
            : formData?.shippingAddress?.country;

          const required = isFieldRequired(field);
          const { pattern, example } = getZipCodePattern(countryKey);

          const isValid = value ? pattern.test(value) : false;

          newValidations[key] = {
            isValid: value
              ? pattern.test(value)
              : !required, // if optional and empty → valid
            message: value
              ? pattern.test(value)
                ? t('checkout.validation.validFormat')
                : t('checkout.validation.invalidFormat', { variables: { example } })
              : "",
          };
        }
      }
      // Special case: Brazil state must be selected
      else if (key === "billingState" && formData?.billingAddress?.country === "BR") {
        const isValid = !!value && value.trim().length > 0;
        newValidations[key] = {
          isValid,
          message: isValid
            ? t('checkout.validation.validField')
            : t('paymentForms.pix.selectStateError'),
        };
      }
      // Generic non-empty validation
      else {
        const required = isFieldRequired(field);

        const isValid = required
          ? value?.trim().length > 0
          : true; // optional field always valid
        newValidations[key] = { isValid };
      }
    });
    const emailValue = formData?.email || "";
    const stateValue = formData?.billingAddress?.state?.trim();

    setValidations((prev) => ({
      ...prev,
      ...newValidations,
      firstName: {
        isValid: isFieldRequired("FIRST_NAME")
          ? formData?.firstName?.trim().length > 0
          : true,
      },
      lastName: {
        isValid: isFieldRequired("LAST_NAME")
          ? formData?.lastName?.trim().length > 0
          : true,
      },
      email: {
        isValid: isFieldRequired("EMAIL")
          ? emailValue ? emailPattern.test(emailValue) : false
          : emailValue ? emailPattern.test(emailValue) : true,
        message: emailValue
          ? emailPattern.test(emailValue)
            ? t('checkout.validation.validField')
            : t('checkout.validation.invalidEmail')
          : "",
      },

      // ✅ BRL condition
      ...(isBRLCurrency && {
        billingState: {
          isValid: isFieldRequired("BILLING_STATE")
            ? stateValue ? true : false
            : true,
          message: stateValue
            ? t('checkout.validation.validField')
            : "",
        },
      }),
    }));
  }, [formData, checkoutPageFields, t]);


  const handleInputChange = (field, value) => {
    const newData = { ...formData };
    if (field.includes(".")) {
      const [section, key] = field.split(".");
      newData[section] = {
        ...newData[section],
        [key]: value,
      };
    } else {
      newData[field] = value;
    }

    // Keep react-hook-form's internal state in sync for programmatic updates
    // (e.g. IP-based country preselection), not only user input handlers.
    setValue(field, value, { shouldValidate: true });

    if (field.startsWith("billingAddress.") && newData.useSameShippingAddress) {
      const key = field.split(".")[1];
      newData.shippingAddress = {
        ...newData.shippingAddress,
        [key]: value,
      };
      setValue(`shippingAddress.${key}`, value, { shouldValidate: true });
    }

    onChange(newData);
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;

    let newData = {
      ...formData,
      useSameShippingAddress: checked,
    };

    if (checked) {
      newData.shippingAddress = { ...newData.billingAddress };

      Object.entries(newData.billingAddress).forEach(([key, value]) => {
        setValue(`shippingAddress.${key}`, value, { shouldValidate: true });
      });

    } else {
      const emptyShipping = {
        firstName: "",
        lastName: "",
        street: "",
        addressLine2: "",
        city: "",
        zipCode: "",
        state: "",
        country: "",
      };

      newData.shippingAddress = emptyShipping;

      Object.keys(emptyShipping).forEach((key) => {
        setValue(`shippingAddress.${key}`, "", { shouldValidate: true });
      });
    }

    onChange(newData);
  };


  useEffect(() => {
    if (checkoutPageFields) {
      const hasShippingFields = [
        "SHIPPING_FIRST_NAME",
        "SHIPPING_LAST_NAME",
        "SHIPPING_ADDRESS_LINE_1",
        "SHIPPING_ADDRESS_LINE_2",
        "SHIPPING_CITY",
        "SHIPPING_ZIPCODE",
        "SHIPPING_COUNTRY",
      ].some((field) => checkoutPageFields?.includes(field));

      setShippingAddressEnabled(hasShippingFields);
    }
  }, [checkoutPageFields]);


  const shouldShowField = (field) => checkoutPageFields?.includes(field);

  return (
    <>
      <FormSection>
        <FormRow>
          <FormGroup>
            <Label htmlFor="firstName">
              {t('checkout.customerForm.firstName')}
              {isFieldRequired("FIRST_NAME") && <Required>*</Required>}
            </Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="firstName"
                  disabled={disabled}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("firstName", e.target.value);
                  }}
                />
              )}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="lastName">
              {t('checkout.customerForm.lastName')}
              {isFieldRequired("LAST_NAME") && <Required>*</Required>}
            </Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="lastName"
                  disabled={disabled}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("lastName", e.target.value);
                  }}
                />
              )}
            />
          </FormGroup>
        </FormRow>
        <FormRow>
          <FormGroup>
            <Label htmlFor="email">
              {t('checkout.customerForm.email')}
                {isFieldRequired("EMAIL") && <Required>*</Required>}
              {/* {email && (
                <ValidationStatus $isValid={validations?.email?.isValid}>
                  {validations?.email?.isValid ? "✓" : "✗"}
                </ValidationStatus>
              )} */}
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="email"
                  type="email"
                  disabled={disabled}
                  $isValid={email && validations?.email?.isValid}
                  $isInvalid={email && !validations?.email?.isValid}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("email", e.target.value);
                  }}
                />
              )}
            />
            <ValidationMessage $isValid={validations?.email?.isValid}>
              {validations?.email?.message}
            </ValidationMessage>
          </FormGroup>
        </FormRow>
      </FormSection>
      {checkoutPageFields != undefined && checkoutPageFields.length > 0 && (
        <>

          <FormSection>
            <SectionTitle>{t('checkout.customerForm.billingAddress')}</SectionTitle>
            {(shouldShowField("BILLING_FIRST_NAME") || shouldShowField("BILLING_LAST_NAME")) && (
              <FormRow>
                {shouldShowField("BILLING_FIRST_NAME") && (
                  <FormGroup>
                    <Label htmlFor="billingFirstName">
                      {t('checkout.customerForm.firstName')}
                      {isFieldRequired("BILLING_FIRST_NAME") && <Required>*</Required>}
                    </Label>
                    <Controller
                      name="billingAddress.firstName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="billingFirstName"
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange(
                              "billingAddress.firstName",
                              e.target.value
                            );
                          }}
                        />
                      )}
                    />
                  </FormGroup>
                )}
                {shouldShowField("BILLING_LAST_NAME") && (
                  <FormGroup>
                    <Label htmlFor="billingLastName">{t('checkout.customerForm.lastName')}{isFieldRequired("BILLING_LAST_NAME") && <Required>*</Required>}</Label>
                    <Controller
                      name="billingAddress.lastName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="billingLastName"
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange(
                              "billingAddress.lastName",
                              e.target.value
                            );
                          }}
                        />
                      )}
                    />
                  </FormGroup>
                )}
              </FormRow>
            )}
            {shouldShowField("BILLING_ADDRESS_LINE_1") && (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="billingAddressLine1">
                    {t('checkout.customerForm.street')}
                    {isFieldRequired("BILLING_ADDRESS_LINE_1") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="billingAddress.street"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="billingAddressLine1"
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "billingAddress.street",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </FormRow>
            )}
            {shouldShowField("BILLING_ADDRESS_LINE_2") && (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="billingAddressLine2">
                    {t('checkout.customerForm.addressLine2')}
                    {isFieldRequired("BILLING_ADDRESS_LINE_2") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="billingAddress.addressLine2"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="billingAddressLine2"
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "billingAddress.addressLine2",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </FormRow>
            )}
            {(shouldShowField("BILLING_CITY") || shouldShowField("BILLING_ZIPCODE")) && (
              <FormRow>
                {shouldShowField("BILLING_CITY") && (
                  <FormGroup>
                    <Label htmlFor="billingCity">
                      {t('checkout.customerForm.city')}
                      {isFieldRequired("BILLING_CITY") && <Required>*</Required>}</Label>
                    <Controller
                      name="billingAddress.city"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="billingCity"
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange("billingAddress.city", e.target.value);
                          }}
                        />
                      )}
                    />
                  </FormGroup>
                )}
                {shouldShowField("BILLING_ZIPCODE") && (
                  <FormGroup>
                    <Label htmlFor="billingZipCode">
                      {t('checkout.customerForm.zipCode')}
                      {isFieldRequired("BILLING_ZIPCODE") && <Required>*</Required>}
                      {billingZipCode && billingCountry && (
                        <ValidationStatus $isValid={validations?.billingZipCode?.isValid}>
                          {validations?.billingZipCode?.isValid ? "✓" : "✗"}
                        </ValidationStatus>
                      )}
                    </Label>
                    <Controller
                      name="billingAddress.zipCode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="billingZipCode"
                          disabled={disabled}
                          $isValid={billingZipCode && validations?.billingZipCode?.isValid}
                          $isInvalid={billingZipCode && !validations?.billingZipCode?.isValid}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange(
                              "billingAddress.zipCode",
                              e.target.value
                            );
                          }}
                        />
                      )}
                    />
                    <ValidationMessage $isValid={validations?.billingZipCode?.isValid}>
                      {validations?.billingZipCode?.message}
                    </ValidationMessage>
                  </FormGroup>
                )}
              </FormRow>
            )}
            {/* Show state field for BRL currency or when configured in checkout fields */}
            {(isBRLCurrency || shouldShowField("BILLING_STATE")) && (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="billingState">
                    {t('checkout.customerForm.state')}
                    {isFieldRequired("BILLING_STATE") && <Required>*</Required>}
                    {/* {isBRLCurrency && billingState && (
                      <ValidationStatus $isValid={validations?.billingState?.isValid}>
                        {validations?.billingState?.isValid ? "✓" : "✗"}
                      </ValidationStatus>
                    )} */}
                  </Label>
                  <Controller
                    name="billingAddress.state"
                    control={control}
                    render={({ field }) => (
                      isBRLCurrency ? (
                        <Select
                          {...field}
                          id="billingState"
                          disabled={disabled}
                          $isValid={billingState && validations?.billingState?.isValid}
                          $isInvalid={billingState && !validations?.billingState?.isValid}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange(
                              "billingAddress.state",
                              e.target.value
                            );
                          }}
                        >
                          <option value="">{t('checkout.customerForm.selectState')}</option>
                          {brazilStates.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.name}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          {...field}
                          id="billingState"
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e);
                            handleInputChange("billingAddress.state", e.target.value);
                          }}
                        />
                      )
                    )}
                  />
                  {isBRLCurrency && (
                    <ValidationMessage $isValid={validations?.billingState?.isValid}>
                      {validations?.billingState?.message}
                    </ValidationMessage>
                  )}
                </FormGroup>
              </FormRow>
            )}
            {shouldShowField("BILLING_COUNTRY") && (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="billingCountry">
                    {t('checkout.customerForm.country')}
                    {isFieldRequired("BILLING_COUNTRY") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="billingAddress.country"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        id="billingCountry"
                        disabled={isBRLCurrency || disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!isBRLCurrency) {
                            handleInputChange(
                              "billingAddress.country",
                              e.target.value
                            );
                          }
                        }}
                      >
                        {isBRLCurrency ? (
                          <>
                            <option value="BR">Brasil</option>
                          </>
                        ) : (
                          <>
                            <option value="">{t('checkout.customerForm.selectCountry')}</option>
                            {allCountries.map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.name}
                              </option>
                            ))}
                          </>
                        )}
                      </Select>
                    )}
                  />
                </FormGroup>
              </FormRow>
            )}
            {shippingAddressEnabled && (
              <Checkbox>
                <input
                  type="checkbox"
                  disabled={disabled}
                  id="useSameShippingAddress"
                  checked={formData.useSameShippingAddress}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="useSameShippingAddress">
                  {t('checkout.customerForm.useSameAddress')}
                </label>
              </Checkbox>
            )}
          </FormSection>
        </>

      )}

      {!formData.useSameShippingAddress && (
        <FormSection>
          {shippingAddressEnabled && (
            <SectionTitle>{t('checkout.customerForm.shippingAddress')}</SectionTitle>
          )}
          {(shouldShowField("SHIPPING_FIRST_NAME") || shouldShowField("SHIPPING_LAST_NAME")) && (
            <FormRow>
              {shouldShowField("SHIPPING_FIRST_NAME") && (
                <FormGroup>
                  <Label htmlFor="shippingFirstName">
                    {t('checkout.customerForm.firstName')}
                    {isFieldRequired("SHIPPING_FIRST_NAME") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="shippingAddress.firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="shippingFirstName"
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "shippingAddress.firstName",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
              )}
              {shouldShowField("SHIPPING_LAST_NAME") && (
                <FormGroup>
                  <Label htmlFor="shippingLastName">
                    {t('checkout.customerForm.lastName')}
                    {isFieldRequired("SHIPPING_LAST_NAME") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="shippingAddress.lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="shippingLastName"
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "shippingAddress.lastName",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
              )}
            </FormRow>
          )}
          {shouldShowField("SHIPPING_ADDRESS_LINE_1") && (
            <FormRow>
              <FormGroup>
                <Label htmlFor="shippingAddressLine1">{t('checkout.customerForm.street')}
                  {isFieldRequired("SHIPPING_ADDRESS_LINE_1") && <Required>*</Required>}
                </Label>
                <Controller
                  name="shippingAddress.street"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="shippingAddressLine1"
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e);
                        handleInputChange(
                          "shippingAddress.street",
                          e.target.value
                        );
                      }}
                    />
                  )}
                />
              </FormGroup>
            </FormRow>
          )}
          {shouldShowField("SHIPPING_ADDRESS_LINE_2") && (
            <FormRow>
              <FormGroup>
                <Label htmlFor="shippingAddressLine2">{t('checkout.customerForm.addressLine2')}
                  {isFieldRequired("SHIPPING_ADDRESS_LINE_2") && <Required>*</Required>}
                </Label>
                <Controller
                  name="shippingAddress.addressLine2"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="shippingAddressLine2"
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e);
                        handleInputChange(
                          "shippingAddress.addressLine2",
                          e.target.value
                        );
                      }}
                    />
                  )}
                />
              </FormGroup>
            </FormRow>
          )}
          {(shouldShowField("SHIPPING_CITY") || shouldShowField("SHIPPING_ZIPCODE")) && (
            <FormRow>
              {shouldShowField("SHIPPING_CITY") && (
                <FormGroup>
                  <Label htmlFor="shippingCity">{t('checkout.customerForm.city')}
                    {isFieldRequired("SHIPPING_CITY") && <Required>*</Required>}
                  </Label>
                  <Controller
                    name="shippingAddress.city"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="shippingCity"
                        disabled={disabled}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "shippingAddress.city",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
              )}
              {shouldShowField("SHIPPING_ZIPCODE") && (
                <FormGroup>
                  <Label htmlFor="shippingZipCode">
                    {t('checkout.customerForm.zipCode')}
                    {isFieldRequired("SHIPPING_ZIPCODE") && <Required>*</Required>}
                    {shippingZipCode && shippingCountry && (
                      <ValidationStatus $isValid={validations?.shippingZipCode?.isValid}>
                        {validations?.shippingZipCode?.isValid ? "✓" : "✗"}
                      </ValidationStatus>
                    )}
                  </Label>
                  <Controller
                    name="shippingAddress.zipCode"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="shippingZipCode"
                        disabled={disabled}
                        $isValid={shippingZipCode && validations?.shippingZipCode?.isValid}
                        $isInvalid={shippingZipCode && !validations?.shippingZipCode?.isValid}
                        onChange={(e) => {
                          field.onChange(e);
                          handleInputChange(
                            "shippingAddress.zipCode",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  />
                  <ValidationMessage $isValid={validations?.shippingZipCode?.isValid}>
                    {validations?.shippingZipCode?.message}
                  </ValidationMessage>
                </FormGroup>
              )}
            </FormRow>
          )}
          {shouldShowField("SHIPPING_STATE") && (
            <FormRow>
              <FormGroup>
                <Label htmlFor="shippingState">{t('checkout.customerForm.state')}
                  {isFieldRequired("SHIPPING_STATE") && <Required>*</Required>}
                </Label>
                <Controller
                  name="shippingAddress.state"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="shippingState"
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e);
                        handleInputChange(
                          "shippingAddress.state",
                          e.target.value
                        );
                      }}
                    />
                  )}
                />
              </FormGroup>
            </FormRow>
          )}
          {shouldShowField("SHIPPING_COUNTRY") && (
            <FormRow>
              <FormGroup>
                <Label htmlFor="shippingCountry">{t('checkout.customerForm.country')}
                  {isFieldRequired("SHIPPING_COUNTRY") && <Required>*</Required>}
                </Label>
                <Controller
                  name="shippingAddress.country"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="shippingCountry"
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e);
                        handleInputChange(
                          "shippingAddress.country",
                          e.target.value
                        );
                      }}
                    >
                      <option value="">{t('checkout.customerForm.selectCountry')}</option>
                      {allCountries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </FormGroup>
            </FormRow>
          )}
        </FormSection>
      )}
    </>
  );
};

export default CustomerInfoForm;
